import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import publicClient from '../api/publicClient';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // ProtectedRoute stashes the intended URL here when it bounces an
  // unauthenticated visitor. For a kiosk QR scan this is /clock?kt=xxx —
  // send them back there after login so they don't have to re-scan.
  const fromRaw = location.state?.from;
  const from = typeof fromRaw === 'string' && fromRaw.startsWith('/') && !fromRaw.startsWith('//')
    ? fromRaw
    : null;

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      return toast.error('Enter your email above first, then tap "Forgot password".');
    }
    setResetting(true);
    try {
      const { data } = await publicClient.post('/api/auth/forgot-password', { email: email.trim() });
      toast.success(data.message || 'If that email is on file, a reset link is on its way.', { duration: 6000 });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send reset link. Try again.');
    } finally {
      setResetting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await login(email, password);
      toast.success('Welcome back!');
      // If ProtectedRoute told us where the user was trying to go
      // (e.g. /clock?kt=xxx from a QR scan), send them straight there.
      // Otherwise fall back to the default landing per role/status.
      if (from) {
        navigate(from, { replace: true });
      } else {
        // Hired candidates still working through clearance / onboarding
        // land on the /my hub instead of the admin dashboard.
        const newHireStatuses = ['pending_clearance', 'pending_hire', 'onboarding'];
        if (newHireStatuses.includes(result?.user?.status)) {
          navigate('/my');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-xl font-bold text-white">BBC</span>
          </div>
          <h1 className="text-2xl font-bold text-white">BBC People Portal</h1>
          <p className="text-gray-400 mt-1">Bright Beginnings Childcare</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-gray-800 rounded-2xl p-8 shadow-xl">
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-xl border border-gray-600 bg-gray-700 px-4 py-2.5 text-white placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 transition-colors"
              placeholder="you@wearebbc.com"
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-xl border border-gray-600 bg-gray-700 px-4 py-2.5 pr-12 text-white placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 transition-colors"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-400 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-orange-500 text-white font-semibold py-2.5 rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-50 shadow-lg shadow-orange-500/25"
          >
            {submitting ? 'Signing in...' : 'Sign In'}
          </button>
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={resetting}
            className="w-full text-center text-sm text-gray-400 hover:text-orange-400 mt-4 transition-colors disabled:opacity-50"
          >
            {resetting ? 'Sending reset link…' : 'Forgot password?'}
          </button>
        </form>

        <p className="text-center text-gray-600 text-xs mt-6">
          Bright Beginnings Childcare &mdash; Confidential
        </p>
      </div>
    </div>
  );
}
